/**
 * Toaster Component
 *
 * Renders toast notifications from the toast store.
 */

import { useToast } from '@/hooks/use-toast'
import { X, CheckCircle, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

export function Toaster() {
  const { toasts, dismiss } = useToast()

  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'flex items-start gap-3 rounded-lg border p-4 shadow-lg transition-all animate-in slide-in-from-right-full',
            toast.variant === 'destructive'
              ? 'border-destructive/50 bg-destructive text-destructive-foreground'
              : 'border-border bg-background text-foreground'
          )}
        >
          {/* Icon */}
          <div className="shrink-0 mt-0.5">
            {toast.variant === 'destructive' ? (
              <AlertCircle className="h-5 w-5" />
            ) : (
              <CheckCircle className="h-5 w-5 text-green-500" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {toast.title && (
              <div className="font-semibold text-sm">{toast.title}</div>
            )}
            {toast.description && (
              <div className={cn(
                'text-sm',
                toast.variant === 'destructive'
                  ? 'text-destructive-foreground/90'
                  : 'text-muted-foreground'
              )}>
                {toast.description}
              </div>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={() => dismiss(toast.id)}
            className={cn(
              'shrink-0 rounded-md p-1 hover:bg-black/10 dark:hover:bg-white/10 transition-colors',
              toast.variant === 'destructive' && 'hover:bg-white/20'
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  )
}
